import { normalizeYoutubeInput } from "@/shared/utils/youtube/youtubeEmbedUrl";
import { PublishedCard } from "../types";
import {
  CreateCardTranslate,
  normalizeNewsContent,
  uploadFileOrReturnPublicUrl,
  uploadFileOrReturnUrl,
  uploadIfNeeded,
} from "./createCardTransformShared";
import type { PageData } from "./useTemplatePageHelpers";

/* Builds the final payload for cards created from a template and submits it.
 * This keeps file uploads, page normalization, and template-specific payload shaping
 * out of `UseTemplateModal`, so the modal can stay focused on UI flow.
 */
interface SubmitTemplateCardParams {
  card: PublishedCard;
  pagesData: PageData[];
  usesEndingImage: boolean;
  currentBusinessId?: number | string;
  currentGtpId?: number | string;
  createMainCard: (payload: any) => Promise<unknown> | unknown;
  t: CreateCardTranslate;
}

const normalizeTypeKey = (type?: string) =>
  (type || "").replace(/_/g, "").replace(/\s+/g, "").toLowerCase().trim();

/** Upload local/base64 media in a single page before POSTing to the API. */
export const preparePageContentForSubmit = async (
  type: string,
  rawContent: any,
  options: { usesEndingImage?: boolean; uploadModule?: string } = {}
): Promise<any> => {
  const { usesEndingImage = false, uploadModule = "Card" } = options;
  const typeKey = normalizeTypeKey(type);

  if (typeKey === "news" || type === "News") {
    const newsItems = normalizeNewsContent(rawContent);
    return Promise.all(
      newsItems.map(async (item) => ({
        title: item.title || "",
        description: item.description || "",
        url: await uploadFileOrReturnUrl(item.url, uploadModule),
      }))
    );
  }

  const content: any = { ...(rawContent || {}) };

  if (type === "Image" || type === "ImageGrid" || type === "Image_Grid") {
    let images: string[] = [];
    if (Array.isArray(content.images)) {
      images = content.images;
    } else if (Array.isArray(content.url)) {
      images = content.url;
    } else if (content.imageUrl) {
      images = [content.imageUrl];
    } else if (typeof content.url === "string") {
      images = [content.url];
    }

    const uploadedImages = (
      await Promise.all(images.map((img) => uploadIfNeeded(img, uploadModule)))
    ).filter(Boolean);

    return {
      url: uploadedImages,
      description: content.description || "",
      ...(content.is_template ? { is_template: true } : {}),
    };
  }

  if (typeKey === "postcard" || type === "PostCard" || type === "Post_Card") {
    if (content.url) {
      content.url = await uploadFileOrReturnUrl(content.url, uploadModule);
    }
    if (content.signature) {
      content.signature = await uploadFileOrReturnUrl(content.signature, uploadModule);
    }
  }

  if (typeKey === "audio" || type === "Audio") {
    const rawAudioUrl =
      (typeof content.audioUrl === "string" && content.audioUrl) ||
      (typeof content.url === "string" && content.url) ||
      (typeof content.audio_url === "string" && content.audio_url) ||
      "";
    const uploadedAudioUrl = rawAudioUrl ? await uploadIfNeeded(rawAudioUrl, uploadModule) : "";

    const rawImageUrl = typeof content.image_url === "string" ? content.image_url : "";
    const uploadedImageUrl = rawImageUrl
      ? await uploadFileOrReturnPublicUrl(rawImageUrl, uploadModule)
      : "";

    if (usesEndingImage) {
      const uploadedLyricsUrl =
        content.lyricsUrl || content.lyrics_url
          ? await uploadFileOrReturnUrl(content.lyricsUrl || content.lyrics_url, uploadModule)
          : "";

      return {
        url: uploadedAudioUrl,
        lyrics_url: uploadedLyricsUrl || "",
        song_title: content.songTitle || content.song_title || content.title || "",
        artist_name: content.artistName || content.artist_name || "",
        description: content.description || "",
        lyrics_text: content.lyrics_text || content.lyricsText || "",
        ...(uploadedImageUrl ? { image_url: uploadedImageUrl } : {}),
        ...(content.is_template ? { is_template: true } : {}),
      };
    }

    return {
      url: uploadedAudioUrl,
      description: content.description || "",
      ...(uploadedImageUrl ? { image_url: uploadedImageUrl } : {}),
      ...(content.is_template ? { is_template: true } : {}),
    };
  }

  if (typeKey === "video" || type === "Video") {
    const rawVideoUrl = content.videoUrl ?? content.url ?? content.video_url ?? "";
    const uploadedVideoUrl = rawVideoUrl
      ? await uploadFileOrReturnUrl(rawVideoUrl, uploadModule)
      : "";

    return {
      url: uploadedVideoUrl,
      description: content.description || "",
      ...(content.is_template ? { is_template: true } : {}),
    };
  }

  if (typeKey === "instrument" || type === "Instrument") {
    const uploadedInstrumentUrl = await uploadFileOrReturnUrl(content.url, uploadModule);
    content.url = uploadedInstrumentUrl;
    content.artist_name = content.artistName || content.artist_name || "";
  }

  if (typeKey === "about" || type === "About") {
    const uploadedDescriptionImage = await uploadFileOrReturnUrl(
      content.descriptionImage || content.description_image,
      uploadModule
    );
    const uploadedExtraDescriptionImage = await uploadFileOrReturnUrl(
      content.extraDescriptionImage || content.extra_description_image,
      uploadModule
    );
    const uploadedMomentImage = await uploadFileOrReturnUrl(
      content.momentImage || content.moment_image,
      uploadModule
    );

    return {
      title: content.title || "",
      description: content.description || "",
      description_image: uploadedDescriptionImage,
      extra_description: content.extraDescription || content.extra_description || "",
      extra_description_image: uploadedExtraDescriptionImage,
      mood_note: content.moodNote || content.mood_note || "",
      moment_image: uploadedMomentImage,
      moments: Array.isArray(content.moments)
        ? content.moments.filter((item: string) => item?.trim())
        : [],
      credits: Array.isArray(content.credits)
        ? content.credits.filter((item: string) => item?.trim())
        : [],
      ...(content.is_template ? { is_template: true } : {}),
    };
  }

  if (typeKey === "bonus" || type === "Bonus") {
    const uploadedVoiceNote = await uploadFileOrReturnUrl(
      content.voiceNote || content.voice_note,
      uploadModule
    );
    const uploadedImage = await uploadFileOrReturnUrl(content.image, uploadModule);
    const uploadedDemoSnippet = await uploadFileOrReturnUrl(
      content.demoSnippet || content.demo_snippet,
      uploadModule
    );

    return {
      title: content.title || "",
      description: content.description || "",
      voice_note: uploadedVoiceNote,
      image: uploadedImage,
      demo_snippet: uploadedDemoSnippet,
      card_links: Array.isArray(content.cardLinks || content.card_links)
        ? content.cardLinks || content.card_links
        : [],
      ...(content.is_template ? { is_template: true } : {}),
    };
  }

  if (typeKey === "youtube" || type === "Youtube") {
    content.url = typeof content.url === "string" ? normalizeYoutubeInput(content.url) : "";
  }

  return content;
};

const getEndingImageUrlFromContent = (content: any) => {
  if (!content) return "";
  if (typeof content.imageUrl === "string" && content.imageUrl.trim()) {
    return content.imageUrl;
  }
  if (typeof content.url === "string" && content.url.trim()) {
    return content.url;
  }
  if (Array.isArray(content.url) && typeof content.url[0] === "string") {
    return content.url[0] || "";
  }
  return "";
};

export const submitTemplateCard = async ({
  card,
  pagesData,
  usesEndingImage,
  currentBusinessId,
  currentGtpId,
  createMainCard,
  t,
}: SubmitTemplateCardParams) => {
  const templatePriceValue = Number(card.templatePurchasePrice);
  const coverImageUrl = await uploadIfNeeded(
    pagesData[0]?.content?.imageUrl || card.coverImageUrl || ""
  );

  const songsPayload = (() => {
    const songsPage = pagesData.find((page) => (page.type || "").toLowerCase() === "songs");
    const songs = songsPage?.content?.songs || [];
    return Array.isArray(songs) ? songs.map((song: string) => song?.trim()).filter(Boolean) : [];
  })();

  const endingImagePage = usesEndingImage
    ? pagesData.find((page, index) => index > 0 && normalizeTypeKey(page.type) === "endingimage")
    : undefined;
  const endingImageUrl = endingImagePage
    ? await uploadIfNeeded(getEndingImageUrlFromContent(endingImagePage.content))
    : "";

  const isBMCardTemplate =
    card.template === t("bm_card_template", { ns: "taascard" }) ||
    card.template === "BM Card Template";
  const isSingleCardTemplate =
    card.template === t("single_card_template", { ns: "taascard" }) ||
    card.template === "Single Card Template";
  const isKIndieCardTemplate =
    card.template === t("k_indie_card_template", { ns: "taascard" }) ||
    card.template === "K-Indie Card Template";
  const isDemoCardTemplate =
    card.template === t("demo_card_template", { ns: "taascard" }) ||
    card.template === "Demo Card Template";
  const isSeasonCardTemplate =
    card.template === t("season_card_template", { ns: "taascard" }) ||
    card.template === "Season Card Template";

  const findSealTimeFromPage = (typeKeys: string[]) => {
    const page = pagesData.find((p) => typeKeys.includes(normalizeTypeKey(p.type)));
    const fromPage =
      typeof page?.content?.seal_time === "string" ? page.content.seal_time.trim() : "";
    if (fromPage) return fromPage;
    const fromCard = typeof card.seal_time === "string" ? card.seal_time.trim() : "";
    return fromCard;
  };

  const bmSealTime = isBMCardTemplate ? findSealTimeFromPage(["bmcard"]) : "";
  const singleFamilySealTime =
    isSingleCardTemplate || isKIndieCardTemplate
      ? findSealTimeFromPage(["singlecard", "kindiecard"])
      : "";
  const topLevelSealTime = bmSealTime || singleFamilySealTime;

  const pages = await Promise.all(
    pagesData
      .slice(1)
      .filter(
        (page) =>
          (page.type || "").toLowerCase() !== "songs" &&
          normalizeTypeKey(page.type) !== "endingimage"
      )
      .map(async (page, index) => {
        const numericPageId = Number(page.id);
        const idPayload =
          page.id !== undefined && Number.isFinite(numericPageId) ? { id: numericPageId } : {};

        let type = page.type;
        const content: any = Array.isArray(page.content)
          ? [...page.content]
          : { ...(page.content || {}), is_template: true };

        if (type === "Image" || type === "ImageGrid" || type === "Image_Grid") {
          let images: string[] = [];
          if (Array.isArray(content.images)) {
            images = content.images;
          } else if (Array.isArray(content.url)) {
            images = content.url;
          } else if (content.imageUrl) {
            images = [content.imageUrl];
          } else if (typeof content.url === "string") {
            images = [content.url];
          }

          const uploadedImages = (
            await Promise.all(images.map((img) => uploadIfNeeded(img)))
          ).filter(Boolean);

          return {
            ...idPayload,
            order: index,
            type,
            content: {
              url: uploadedImages,
              description: content.description || "",
              is_template: true,
            },
            assets: [],
          };
        }

        if (
          type === "TaasCard" ||
          type === "Taas_Card" ||
          type === "DiamondCard" ||
          type === "Diamond_Card" ||
          type === "CloverCard" ||
          type === "Clover_Card"
        ) {
          if (content.primary_thumbnail) {
            content.primary_thumbnail = await uploadFileOrReturnUrl(content.primary_thumbnail);
          }

          if (Array.isArray(content.gallery_images)) {
            content.gallery_images = await Promise.all(
              content.gallery_images.map((item: any) => uploadFileOrReturnUrl(item))
            );
          }

          if (Array.isArray(content.products)) {
            content.products = await Promise.all(
              content.products.map(async (product: any) => {
                const processedProduct = { ...product };

                if (!processedProduct.filename && processedProduct.product_url instanceof File) {
                  processedProduct.filename = processedProduct.product_url.name;
                }

                if (processedProduct.product_url instanceof File) {
                  processedProduct.product_url = await uploadFileOrReturnUrl(
                    processedProduct.product_url
                  );
                }

                if (processedProduct.thumbnail_url instanceof File) {
                  processedProduct.thumbnail_url = await uploadFileOrReturnUrl(
                    processedProduct.thumbnail_url
                  );
                }

                return processedProduct;
              })
            );
          }
        }

        if (normalizeTypeKey(type) === "postcard" || type === "PostCard" || type === "Post_Card") {
          if (content.url) {
            content.url = await uploadFileOrReturnUrl(content.url);
          }

          if (content.signature) {
            content.signature = await uploadFileOrReturnUrl(content.signature);
          }
        }

        if (normalizeTypeKey(type) === "news" || type === "News") {
          const newsItems = normalizeNewsContent(page.content);
          const uploadedNewsItems = await Promise.all(
            newsItems.map(async (item) => ({
              title: item.title || "",
              description: item.description || "",
              url: await uploadFileOrReturnUrl(item.url),
            }))
          );

          return {
            ...idPayload,
            order: index,
            type,
            content: uploadedNewsItems,
            assets: [],
          };
        }

        if (normalizeTypeKey(type) === "audio" || type === "Audio") {
          // `AudioExperience` stores newly-picked audio as a base64
          // `data:audio/...` URL in `audioUrl` (and the renderer mirrors it
          // into `url`). Optional image is stored as base64 in `image_url`
          // and uploaded here on submit.
          const rawAudioUrl =
            (typeof content.audioUrl === "string" && content.audioUrl) ||
            (typeof content.url === "string" && content.url) ||
            (typeof content.audio_url === "string" && content.audio_url) ||
            "";
          const uploadedAudioUrl = rawAudioUrl ? await uploadIfNeeded(rawAudioUrl) : "";

          const rawImageUrl = typeof content.image_url === "string" ? content.image_url : "";
          const uploadedImageUrl = rawImageUrl
            ? await uploadFileOrReturnPublicUrl(rawImageUrl, "Card")
            : "";

          if (usesEndingImage) {
            const uploadedLyricsUrl =
              content.lyricsUrl || content.lyrics_url
                ? await uploadFileOrReturnUrl(content.lyricsUrl || content.lyrics_url)
                : "";

            return {
              ...idPayload,
              order: index,
              type,
              content: {
                url: uploadedAudioUrl,
                lyrics_url: uploadedLyricsUrl || "",
                song_title: content.songTitle || content.song_title || content.title || "",
                artist_name: content.artistName || content.artist_name || "",
                description: content.description || "",
                lyrics_text: content.lyrics_text || content.lyricsText || "",
                ...(uploadedImageUrl ? { image_url: uploadedImageUrl } : {}),
                is_template: true,
              },
              assets: [],
            };
          }

          return {
            ...idPayload,
            order: index,
            type,
            content: {
              url: uploadedAudioUrl,
              description: content.description || "",
              ...(uploadedImageUrl ? { image_url: uploadedImageUrl } : {}),
              is_template: true,
            },
            assets: [],
          };
        }

        if (normalizeTypeKey(type) === "video" || type === "Video") {
          const rawVideoUrl = content.videoUrl ?? content.url ?? content.video_url ?? "";
          const uploadedVideoUrl = rawVideoUrl ? await uploadFileOrReturnUrl(rawVideoUrl) : "";

          return {
            ...idPayload,
            order: index,
            type,
            content: {
              url: uploadedVideoUrl,
              description: content.description || "",
              is_template: true,
            },
            assets: [],
          };
        }

        if (normalizeTypeKey(type) === "instrument" || type === "Instrument") {
          const uploadedInstrumentUrl = await uploadFileOrReturnUrl(content.url);
          content.url = uploadedInstrumentUrl;
          content.artist_name = content.artistName || content.artist_name || "";
        }

        if (normalizeTypeKey(type) === "about" || type === "About") {
          const uploadedDescriptionImage = await uploadFileOrReturnUrl(
            content.descriptionImage || content.description_image
          );
          const uploadedExtraDescriptionImage = await uploadFileOrReturnUrl(
            content.extraDescriptionImage || content.extra_description_image
          );
          const uploadedMomentImage = await uploadFileOrReturnUrl(
            content.momentImage || content.moment_image
          );

          return {
            ...idPayload,
            order: index,
            type,
            content: {
              title: content.title || "",
              description: content.description || "",
              description_image: uploadedDescriptionImage,
              extra_description: content.extraDescription || content.extra_description || "",
              extra_description_image: uploadedExtraDescriptionImage,
              mood_note: content.moodNote || content.mood_note || "",
              moment_image: uploadedMomentImage,
              moments: Array.isArray(content.moments)
                ? content.moments.filter((item: string) => item?.trim())
                : [],
              credits: Array.isArray(content.credits)
                ? content.credits.filter((item: string) => item?.trim())
                : [],
              is_template: true,
            },
            assets: [],
          };
        }

        if (normalizeTypeKey(type) === "bonus" || type === "Bonus") {
          const uploadedVoiceNote = await uploadFileOrReturnUrl(
            content.voiceNote || content.voice_note
          );
          const uploadedImage = await uploadFileOrReturnUrl(content.image);
          const uploadedDemoSnippet = await uploadFileOrReturnUrl(
            content.demoSnippet || content.demo_snippet
          );

          return {
            ...idPayload,
            order: index,
            type,
            content: {
              title: content.title || "",
              description: content.description || "",
              voice_note: uploadedVoiceNote,
              image: uploadedImage,
              demo_snippet: uploadedDemoSnippet,
              card_links: Array.isArray(content.cardLinks || content.card_links)
                ? content.cardLinks || content.card_links
                : [],
              is_template: true,
            },
            assets: [],
          };
        }

        if (type === "StaiCard" || type === "Stai_Card" || type === "stai_card") {
          if (content.signature) {
            content.signature = await uploadFileOrReturnUrl(content.signature);
          }
        }

        if (type === "FanClubCard" || type === "Fan_Club_Card" || type === "fan_club_card") {
          const uploadedSignature = content.signature
            ? await uploadFileOrReturnUrl(content.signature)
            : "";
          const uploadedLastPageCover = content.last_page_cover
            ? await uploadFileOrReturnUrl(content.last_page_cover)
            : "";

          return {
            ...idPayload,
            order: index,
            type,
            content: {
              talent_name: content.talent_name || "",
              title: content.title || "",
              subtitle: content.subtitle || "",
              description: content.description || "",
              price: content.price,
              edition_year: content.edition_year,
              duration: content.duration,
              start_time: content.start_time || "",
              thankyou_message: content.thankyou_message || "",
              signature: uploadedSignature,
              last_page_cover: uploadedLastPageCover,
              is_template: true,
            },
            assets: [],
          };
        }

        if (type === t("welcome", { ns: "taascard" })) {
          const uploadedMediaUrl = content.media_url
            ? await uploadFileOrReturnUrl(content.media_url)
            : "";

          return {
            ...idPayload,
            order: index,
            type: t("welcome", { ns: "taascard" }),
            content: {
              title: content.title || "",
              description: content.content || content.description || "",
              ...(uploadedMediaUrl ? { url: uploadedMediaUrl } : {}),
              is_template: true,
            },
            assets: [],
          };
        }

        if (type === "Song" || type === "Song4U_Songs" || type === "Song4U Songs") {
          const rawSongs = content.songs;
          const songs = Array.isArray(rawSongs)
            ? rawSongs.map((song: any) => ({
                name: song?.name ?? "",
                price:
                  song?.price === "" || song?.price === undefined || song?.price === null
                    ? ""
                    : Number(song?.price),
              }))
            : [];

          return {
            ...idPayload,
            order: index,
            type: "Song",
            content: {
              songs,
              is_template: true,
            },
            assets: [],
          };
        }

        if (normalizeTypeKey(type) === "bmcard" || type === "BMCard" || type === "BM_Card") {
          content.signature_url = await uploadFileOrReturnUrl(content.signature_url || "");
          content.thankyou_image = content.thankyou_image
            ? await uploadFileOrReturnUrl(content.thankyou_image)
            : "";
          // BM seal_time is submitted as top-level payload field.
          delete content.seal_time;
          delete content.status;
        }

        if (
          (type === "InstantCard" || type === "Instant_Card" || type === "instant_card") &&
          songsPayload.length > 0
        ) {
          content.songs = songsPayload;
        }

        if (
          topLevelSealTime &&
          (normalizeTypeKey(type) === "singlecard" || normalizeTypeKey(type) === "kindiecard")
        ) {
          content.seal_time = topLevelSealTime;
        }

        return {
          ...idPayload,
          order: index,
          type,
          content,
          assets: [],
        };
      })
  );

  const isBuskingCardTemplate = card.template === t("busking_card_template", { ns: "taascard" });
  const isTaasCardTemplate =
    card.template === t("heart_card_template", { ns: "taascard" }) ||
    card.template === t("spade_card_template", { ns: "taascard" }) ||
    card.template === t("diamond_card_template", { ns: "taascard" }) ||
    card.template === t("clover_card_template", { ns: "taascard" });
  const isFlashCardTemplate = card.template === t("flash_card_template", { ns: "taascard" });
  const isStageCardTemplate = card.template === t("stage_card_template", { ns: "taascard" });
  const isAitCardTemplate = card.template === t("ait_card_template", { ns: "taascard" });
  const isSong4UCardTemplate = card.template === t("song4u_card_template", { ns: "taascard" });
  const isListeningTogetherCardTemplate = card.template === "Listening Together Card Template";
  if (pages.length > 0 && coverImageUrl) {
    pages[0].content = {
      ...pages[0].content,
      thumbnail: coverImageUrl,
      media: isBuskingCardTemplate && coverImageUrl,
      primary_thumbnail: (isTaasCardTemplate || isStageCardTemplate) && coverImageUrl,
      cover_image: isFlashCardTemplate && coverImageUrl,
      ...(isSong4UCardTemplate ? { cover_image: coverImageUrl } : {}),
    };
  }

  let finalPrice = templatePriceValue;
  if (isAitCardTemplate) {
    const aitPage = pagesData.find((page) => normalizeTypeKey(page.type) === "aitcard");
    if (aitPage?.content?.price !== undefined && aitPage.content.price !== "") {
      finalPrice = Number(aitPage.content.price) || 0;
    }
  } else if (isSong4UCardTemplate) {
    const song4uPage = pagesData.find((page) => normalizeTypeKey(page.type) === "song4ucard");
    if (song4uPage?.content?.price !== undefined && song4uPage.content.price !== "") {
      finalPrice = Number(song4uPage.content.price) || 0;
    }
  } else if (isBMCardTemplate) {
    const bmPage = pagesData.find((page) => normalizeTypeKey(page.type) === "bmcard");
    if (bmPage?.content?.price !== undefined && bmPage.content.price !== "") {
      finalPrice = Number(bmPage.content.price) || 0;
    }
  } else if (isListeningTogetherCardTemplate) {
    // The Listening Together card stores its user-set price on the card
    // page itself (`Listen_Together_Card` or, pre-normalization,
    // `Listening_Together_Card`). On EDIT the parent passes the purchased
    // main card whose `templatePurchasePrice` is `undefined`, so without
    // this branch `finalPrice` would resolve to `NaN` and serialize as
    // `null`, which the backend rejects. On CREATE this branch also
    // ensures any in-form price edit is honored instead of silently being
    // overridden by the original template's price.
    const ltPage = pagesData.find((page) => {
      const k = normalizeTypeKey(page.type);
      return k === "listentogethercard" || k === "listeningtogethercard";
    });
    if (ltPage?.content?.price !== undefined && ltPage.content.price !== "") {
      finalPrice = Number(ltPage.content.price) || 0;
    }
  } else if (
    isSingleCardTemplate ||
    isKIndieCardTemplate ||
    isDemoCardTemplate ||
    isSeasonCardTemplate
  ) {
    const shellPage = pagesData.find((page) => {
      const k = normalizeTypeKey(page.type);
      return k === "singlecard" || k === "kindiecard" || k === "democard" || k === "seasoncard";
    });
    if (shellPage?.content?.price !== undefined && shellPage.content.price !== "") {
      finalPrice = Number(shellPage.content.price) || 0;
    }
  }

  const payload = {
    ...(currentBusinessId && { business_id: currentBusinessId }),
    ...(currentGtpId && { gtp_id: currentGtpId }),
    template: card.template || "",
    template_id: card.id || "",
    cover_image_url: coverImageUrl,
    ...(usesEndingImage ? { ending_image_url: endingImageUrl } : {}),
    ...(topLevelSealTime ? { seal_time: topLevelSealTime } : {}),
    ...(isBMCardTemplate ? { seal_time_required: true } : {}),
    price: finalPrice,
    pages,
    status: "Active",
  };

  return await createMainCard(payload);
};
